import { selectNewestMergedRevision } from "@/store/revisions-selector";
import { useAppSelector } from "@/store/store-hooks";
import {
  RegistrationState,
  RevisionType,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { useEffect } from "react";

const POLLING_INTERVAL_MS = 60_000;

/**
 * Polls the backend for new merged revisions every POLLING_INTERVAL_MS milliseconds,
 * and calls the provided callback if a new merged revision is found.
 *
 * @param callback Function that is called when a new merged revision is found.
 */
export function usePollNewMergedRevision(callback: () => void): void {
  const { projectApiClient } = useApiClientContext();
  const originalMergedRevision = useAppSelector(selectNewestMergedRevision);
  const originalMergedRevisionId = originalMergedRevision?.id;

  useEffect(() => {
    async function fetchRevisions(): Promise<void> {
      const revisions = await projectApiClient.getAllRegistrationRevisions();
      // Sort by creation date, to have the newest revision first.
      const newMergedRevision = revisions
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )
        .find(
          (revision) =>
            revision.state === RegistrationState.merged &&
            revision.revisionType === RevisionType.draft,
        );

      if (
        newMergedRevision?.id &&
        originalMergedRevisionId !== newMergedRevision.id
      ) {
        callback();
      }
    }
    const id = setInterval(() => fetchRevisions(), POLLING_INTERVAL_MS);

    return () => clearInterval(id);
  }, [callback, originalMergedRevisionId, projectApiClient]);
}
